import MediaLibrary from "@/components/feature/MediaLibrary";

interface MediaLibraryModalProps {
  /** Bearer token for the authenticated media API. */
  token: string | null;
  /** Dialog title. */
  title?: string;
  /** Optional supporting line under the title. */
  subtitle?: string;
  /** Allow selecting more than one image. */
  multiple?: boolean;
  /** Hard cap when multiple is true. */
  maxSelect?: number;
  /** URLs already in use (highlighted / pre-selected). */
  currentUrls?: string[];
  /** Show the upload controls inside the picker. */
  canUpload?: boolean;
  /** Destination folder for anything uploaded from the picker. */
  uploadFolder?: "listings" | "gallery" | "ads" | "logos" | "avatars" | "media" | "branding";
  /** Called with the chosen URL(s). */
  onSelect: (urls: string[]) => void;
  /** Close the dialog. */
  onClose: () => void;
}

/**
 * Customer-facing wrapper around the shared Media Library.
 *
 * Gives business owners the same “pick from library” experience admins get —
 * they can reuse images already uploaded instead of uploading duplicates.
 */
export default function MediaLibraryModal({
  token,
  title = "Media Library",
  subtitle = "Pick an image you've already uploaded, or add a new one.",
  multiple = false,
  maxSelect = 8,
  currentUrls,
  canUpload = false,
  uploadFolder = "media",
  onSelect,
  onClose,
}: MediaLibraryModalProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/50 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-background-200 bg-background-50"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-background-200 px-6 py-4">
          <div>
            <h2 className="font-heading text-base font-bold text-foreground-950">{title}</h2>
            <p className="mt-0.5 text-xs text-foreground-500">{subtitle}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-900"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-5">
          <MediaLibrary
            token={token}
            scope="owner"
            mode="pick"
            multiple={multiple}
            maxSelect={maxSelect}
            currentUrls={currentUrls}
            canUpload={canUpload}
            uploadFolder={uploadFolder}
            onSelect={onSelect}
            onCancel={onClose}
          />
        </div>
      </div>
    </div>
  );
}